import React from "react"
import type { CSSProperties, ReactNode } from "react"
import cx from "classnames"

const DEFAULT = "font-semibold border rounded-full px-2 py-px inline-block"
const COLORS = {
  gray: {
    solid: "bg-gray-800 border-gray-800 text-white",
    outline: "bg-transparent border-gray-800 text-gray-800",
    light: "bg-gray-100 border-gray-100 text-gray-900"
  },
  green: {
    solid: "bg-green-500 border-green-500 text-white",
    outline: "bg-transparent border-green-600 text-green-600",
    light: "bg-green-100 border-green-100 text-green-800"
  },
  red: {
    solid: "bg-red-500 border-red-500 text-white",
    outline: "bg-transparent border-red-600 text-red-600",
    light: "bg-red-50 border-red-50 text-red-600"
  },
  blue: {
    solid: "bg-blue-500 border-blue-500 text-white",
    outline: "bg-transparent border-blue-600 text-blue-600",
    light: "bg-blue-50 border-blue-50 text-blue-600"
  },
  yellow: {
    solid: "bg-yellow-500 border-yellow-500 text-white",
    outline: "bg-transparent border-yellow-600 text-yellow-600",
    light: "bg-yellow-50 border-yellow-50 text-yellow-600"
  },
  orange: {
    solid: "bg-kit-orange-500 border-kit-orange-500 text-white",
    outline: "bg-transparent border-kit-orange-600 text-kit-orange-600",
    light: "bg-kit-orange-100 border-kit-orange-100 text-kit-orange-800"
  },
  purple: {
    solid: "bg-kit-purple-500 border-kit-purple-500 text-white",
    outline: "bg-transparent border-kit-purple-600 text-kit-purple-600",
    light: "bg-kit-purple-100 border-kit-purple-100 text-kit-purple-800"
  },
  black: {
    solid: "bg-gray-900 border-gray-900 text-white",
    outline: "bg-transparent border-gray-900 text-gray-900",
    light: "bg-gray-900 border-gray-900 text-white"
  },
  white: {
    solid: "bg-white border-white text-gray-900",
    outline: "bg-transparent border-white text-white",
    light: "bg-white border-white text-gray-900"
  }
}

const SIZES = {
  sm: "text-xs leading-4",
  lg: "text-sm leading-5"
}

type BadgeProps = {
  children: ReactNode
  /** @deprecated Use the `color` and `variant` props instead. */
  className?: string
  color?:
    | "gray"
    | "green"
    | "red"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "black"
    | "white"
  variant?: "solid" | "outline" | "light"
  size?: "sm" | "lg"
  /** @deprecated Avoid inline styles; prefer design system props. */
  style?: CSSProperties
  "aria-label"?: string
  "aria-hidden"?: boolean
}

const Badge = ({
  children,
  className: classNameProp,
  color = "gray",
  variant = "solid",
  size = "sm",
  style,
  "aria-label": ariaLabel,
  "aria-hidden": ariaHidden
}: BadgeProps) => {
  const className = cx(
    classNameProp,
    DEFAULT,
    COLORS[color][variant],
    SIZES[size]
  )

  return (
    <span
      className={className}
      style={style}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    >
      {children}
    </span>
  )
}

export default Badge
export { COLORS }
export type { BadgeProps }
